import { useToast } from '@/components/ui/Toast'
import { queryKeys } from '@/lib/queryClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { leadsApi } from '../api/leadsApi'

/**
 * Hook to fetch leads list with pagination and filters
 */
export function useLeads(params = {}) {
  return useQuery({
    queryKey: queryKeys.leads.list(params),
    queryFn: () => leadsApi.getLeads(params),
    keepPreviousData: true, // Keep previous data while fetching new page
  })
}

/**
 * Hook to fetch single lead by UUID
 */
export function useLead(uuid) {
  return useQuery({
    queryKey: queryKeys.leads.detail(uuid),
    queryFn: () => leadsApi.getLead(uuid),
    enabled: !!uuid, // Only fetch if uuid is provided
  })
}

/**
 * Hook to create new lead
 */
export function useCreateLead() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: leadsApi.createLead,
    onSuccess: (data) => {
      // Invalidate leads list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all })
      toast.success({
        title: 'Lead created',
        description: 'Lead has been successfully created.',
      })
    },
    onError: (error) => {
      // Don't show toast for validation errors (handled by form)
      if (error?.error?.details && typeof error.error.details === 'object') {
        return
      }

      toast.error({
        title: 'Failed to create lead',
        description: error.message || 'An error occurred while creating the lead.',
      })
    },
  })
}

/**
 * Hook to update lead
 */
export function useUpdateLead() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ uuid, data }) => leadsApi.updateLead(uuid, data),
    onSuccess: (data, variables) => {
      // Invalidate specific lead to refetch with expand parameter
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.detail(variables.uuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all })
      toast.success({
        title: 'Lead updated',
        description: 'Lead has been successfully updated.',
      })
    },
    onError: (error) => {
      // Don't show toast for validation errors (handled by form)
      if (error?.error?.details && typeof error.error.details === 'object') {
        return
      }

      toast.error({
        title: 'Failed to update lead',
        description: error.message || 'An error occurred while updating the lead.',
      })
    },
  })
}

/**
 * Hook to delete lead
 */
export function useDeleteLead() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: leadsApi.deleteLead,
    onSuccess: () => {
      // Invalidate leads list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all })
      toast.success({
        title: 'Lead deleted',
        description: 'Lead has been successfully deleted.',
      })
    },
    onError: (error) => {
      toast.error({
        title: 'Failed to delete lead',
        description: error.message || 'An error occurred while deleting the lead.',
      })
    },
  })
}

/**
 * Hook to fetch leads statistics
 */
export function useLeadsStats() {
  return useQuery({
    queryKey: queryKeys.leads.stats,
    queryFn: () => leadsApi.getLeadsStats(),
    refetchInterval: 30000, // Refetch every 30 seconds
  })
}

/**
 * Hook to fetch users for autocomplete
 */
export function useUserAutocomplete(query) {
  return useQuery({
    queryKey: queryKeys.users.autocomplete(query),
    queryFn: () => leadsApi.getUserAutocomplete(query),
    enabled: query.length >= 2, // Only search if query has at least 2 characters
  })
}
